import { useState, useEffect, createElement, Fragment } from 'react';
import './TrumpTracker.css';
import PropTypes from 'prop-types';

const CollapsibleSection = ({ title, children, startOpen = false }) => {
  const [isOpen, setIsOpen] = useState(startOpen);
  return (
    <div className="detail-section">
      <button className="collapsible-header" onClick={() => setIsOpen(!isOpen)}>
        <h4>{title}</h4>
        <span className="collapsible-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && <p>{children}</p>}
    </div>
  );
};
CollapsibleSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  startOpen: PropTypes.bool,
};

const sortOptions = [
  { value: 'danger', label: 'Danger Score' },
  { value: 'date', label: 'Publication Date' },
  { value: 'order', label: 'Order #' },
];

function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
}

function TrumpTracker() {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [topicFilter, setTopicFilter] = useState('All');
  const [sortBy, setSortBy] = useState('danger');
  const [sortDir, setSortDir] = useState('desc');
  const [showExplanation, setShowExplanation] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(() => window.innerWidth > 600);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 600) {
        setShowFilters(false);
      } else {
        setShowFilters(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetch('executive_orders_v2.json')
      .then(res => res.json())
      .then(data => setOrders(data));
  }, []);

  const topics = Array.from(new Set(orders.map(o => o.Topic))).sort();

  let filteredOrders = orders;
  if (topicFilter !== 'All') {
    filteredOrders = filteredOrders.filter(o => o.Topic === topicFilter);
  }
  if (searchTerm.trim() !== '') {
    const lower = searchTerm.toLowerCase();
    filteredOrders = filteredOrders.filter(order =>
      Object.values(order).some(val =>
        String(val).toLowerCase().includes(lower)
      )
    );
  }
  filteredOrders = [...filteredOrders].sort((a, b) => {
    if (sortBy === 'danger') {
      return sortDir === 'asc' ? a['Danger Score'] - b['Danger Score'] : b['Danger Score'] - a['Danger Score'];
    } else if (sortBy === 'date') {
      const parseDate = (d) => new Date(d);
      return sortDir === 'asc'
        ? parseDate(a.publication_date).getTime() - parseDate(b.publication_date).getTime()
        : parseDate(b.publication_date).getTime() - parseDate(a.publication_date).getTime();
    } else if (sortBy === 'order') {
      return sortDir === 'asc' ? a['Order #'] - b['Order #'] : b['Order #'] - a['Order #'];
    } else if (sortBy === 'topic') {
      return sortDir === 'asc' ? a.Topic.localeCompare(b.Topic) : b.Topic.localeCompare(a.Topic);
    } else if (sortBy === 'title') {
      return sortDir === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
    }
    return 0;
  });

  const minSize = 60;
  const maxSize = 120;
  const minScore = Math.min(...orders.map(o => o['Danger Score']));
  const maxScore = Math.max(...orders.map(o => o['Danger Score']));
  const getBubbleSize = (score) => {
    if (maxScore === minScore) return (minSize + maxSize) / 2;
    return minSize + ((score - minScore) / (maxScore - minScore)) * (maxSize - minSize);
  };
  const getBubbleColor = (score) =>
    score > 70 ? '#ff4444' : score > 50 ? '#ffbb33' : '#00C851';

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  function highlight(text) {
    if (!searchTerm.trim()) return text;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.split(regex).map((part, i) =>
      regex.test(part) ? <mark key={i} style={{ background: '#ffe066', color: '#222', padding: 0 }}>{part}</mark> : part
    );
  }

  return (
    <div className="App">
      <h1>Trump's Executive Orders</h1>
      {!showExplanation && (
        <button
          className="toggle-filters-btn"
          onClick={() => setShowExplanation(true)}
        >
          Show Explanation
        </button>
      )}
      {showExplanation && (
        <div
          className="danger-score-explanation"
          style={{ maxWidth: 700, margin: '1.5rem auto', textAlign: 'left', background: '#f8f8f8', borderRadius: 8, padding: '1.5rem 2rem', fontSize: '1.1rem', boxShadow: '0 2px 8px rgba(44,62,80,0.04)', position: 'relative', cursor: 'pointer' }}
          onClick={(e) => {
            if (e.target.closest('button')) return;
            setShowExplanation(false);
          }}
        >
          <p>We used an AI assistant to help create the information on this website. For each executive order, the AI researched public information and then used it to build the analysis you see here.</p>
          <h3 style={{ marginTop: '1.2rem' }}>What is the 'Danger Score'?</h3>
          <p>The 'Danger Score' is a number from 0 to 100 that gives a simple estimate of how much an executive order could harm American democracy.</p>
          <p>To get the score, the AI looked at a few key things for each order:</p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li>Does it go against the Constitution or other laws?</li>
            <li>How many people does it affect?</li>
            <li>Does it respect democratic ideas like fairness and equal rights?</li>
            <li>What is its overall effect on the country?</li>
          </ul>
          <p>The higher the score, the greater the potential risk. A high score means we should all pay close attention to that order.</p>
          <button
            style={{
              display: 'block',
              margin: '1.5rem auto 0 auto',
              fontSize: '1.1rem',
              fontWeight: 600,
              padding: '0.5em 1.2em',
              borderRadius: 6,
              border: '1.5px solid #2c3e50',
              background: '#f5f5f5',
              color: '#2c3e50',
              cursor: 'pointer',
            }}
            aria-label="Hide explanation"
            onClick={() => setShowExplanation(false)}
          >
            Hide Explanation
          </button>
        </div>
      )}
      <h2>You can sort by topic, danger score, publication date, or order number.</h2>
      <button
        className="toggle-filters-btn"
        onClick={() => setShowFilters(f => !f)}
        id="toggle-filters-btn"
      >
        {showFilters ? 'Hide Filters' : 'Show Filters'}
      </button>
      {showFilters && (
        <div className="filters-container">
          <label>
            <span style={{ fontWeight: 600, marginRight: 8 }}>Filter by Topic:</span>
            <select value={topicFilter} onChange={e => setTopicFilter(e.target.value)}>
              <option value="All">All</option>
              {topics.map(topic => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={{ fontWeight: 600, marginRight: 8 }}>Sort by:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <button
            className="sort-dir-btn"
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          >
            {sortDir === 'asc' ? '▲ Ascending' : '▼ Descending'}
          </button>
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 8 }}>
            <span style={{
              position: 'absolute',
              left: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888',
              pointerEvents: 'none',
              fontSize: '1.2em',
              zIndex: 2
            }}>
              🔍
            </span>
            <input
              type="text"
              placeholder="Search all fields..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                fontSize: '1.1rem',
                padding: '0.5em 1em 0.5em 2.2em',
                borderRadius: 4,
                border: '1px solid #2c3e50',
                minWidth: 220,
                background: '#fff',
                color: '#2c3e50',
                height: '2.2em',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border 0.2s',
              }}
            />
          </div>
        </div>
      )}
      <div className="orders-table-container">
        <div className="orders-table-header">
          <div
            className="orders-table-col orders-table-col-score orders-table-col-sortable"
            onClick={() => handleSort('danger')}
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center' }}
          >
            {isMobile ? 'Score' : 'Danger Score'}
            {sortBy === 'danger' && (
              <span style={{ marginLeft: 6, fontSize: isMobile ? '0.95em' : '1rem' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
            )}
          </div>
          <div
            className="orders-table-col orders-table-col-topic orders-table-col-sortable"
            onClick={() => handleSort('topic')}
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center' }}
          >
            {isMobile ? 'Topic' : 'Topic'}
            {sortBy === 'topic' && (
              <span style={{ marginLeft: 6, fontSize: isMobile ? '0.95em' : '1rem' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
            )}
          </div>
          <div
            className="orders-table-col orders-table-col-title orders-table-col-sortable"
            onClick={() => handleSort('title')}
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center' }}
          >
            {isMobile ? 'Title' : 'Title'}
            {sortBy === 'title' && (
              <span style={{ marginLeft: 6, fontSize: isMobile ? '0.95em' : '1rem' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
            )}
          </div>
        </div>
        <div className="orders-table-body">
          {filteredOrders.map((order, idx) => (
            <div
              key={order['Order #']}
              className={`orders-table-row${idx % 2 === 0 ? ' even' : ' odd'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedOrder(order)}
            >
              <div className="orders-table-col orders-table-col-score">
                <div
                  className={`bubble ${selectedOrder && selectedOrder['Order #'] === order['Order #'] ? 'selected' : ''}`}
                  style={{
                    background: getBubbleColor(order['Danger Score']),
                    fontSize: getBubbleSize(order['Danger Score']) * 0.32,
                  }}
                  title={order.title}
                >
                  <span className="bubble-score">{order['Danger Score']}</span>
                </div>
              </div>
              <div className="orders-table-col orders-table-col-topic">{order.Topic}</div>
              <div className="orders-table-col orders-table-col-title">{highlight(order.title)}</div>
            </div>
          ))}
        </div>
      </div>
      {selectedOrder && (
        <>
          <div className="modal-backdrop" onClick={() => setSelectedOrder(null)} />
          <div className="bubble-details-modal">
            <div
              className="modal-danger-score"
              style={{
                background: getBubbleColor(selectedOrder['Danger Score'])
              }}
            >
              {selectedOrder['Danger Score']}
            </div>
            <button
              className="close-button"
              onClick={() => setSelectedOrder(null)}
              aria-label="Close"
            >
              ×
            </button>
            <h2>{'Title : '}{highlight(selectedOrder.title)}</h2>
            <div className="order-number-display">Executive Order #{selectedOrder['Order #']}</div>
            <div className="topic-display">Topic: {highlight(selectedOrder.Topic)}</div>
            <CollapsibleSection title="Summary" startOpen>
              {highlight(selectedOrder.Summary)}
            </CollapsibleSection>
            <CollapsibleSection title="Reason for Score">
              {highlight(selectedOrder.Reason)}
            </CollapsibleSection>
            <CollapsibleSection title="Goes Against Constitution or Existing Laws?">
              {highlight(selectedOrder['Goes Against Constitution or Existing Laws?'])}
            </CollapsibleSection>
            <CollapsibleSection title="How the Public Generally Thinks About It?">
              {highlight(selectedOrder['How the Public Generally Thinks About It?'])}
            </CollapsibleSection>
            <CollapsibleSection title="Potential Damage to Current Democracy">
              {highlight(selectedOrder['Potential Damage to Current Democracy'])}
            </CollapsibleSection>
            <div className="detail-section">
              <h4>Date Signed</h4>
              <p>{highlight(selectedOrder.signing_date)}</p>
            </div>
            <a href={selectedOrder.html_url} target="_blank" rel="noopener noreferrer" className="view-order-btn">
              View Full Order
            </a>
          </div>
        </>
      )}
    </div>
  );
}

export default TrumpTracker;